import React from 'react';
import {
  VENUE_TYPES,
  GUEST_COUNT_OPTIONS
} from '../../../constants/bookingConstants';
import { getTodayDate } from '../../../utils/bookingUtils';

const BookingStepEvent = ({
  formData,
  errors,
  handleInputChange,
  availabilityWindow,
  availabilityState,
  searchRole,
  selectedSlotId,
  setSelectedSlotId
}) => (
  <div className="space-y-6">
    <h2 className="text-xl font-semibold text-gray-900 mb-4">Event Details</h2>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Event Date *</label>
        <input
          type="date"
          name="eventDate"
          value={formData.eventDate}
          onChange={handleInputChange}
          min={getTodayDate()}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
            errors.eventDate ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.eventDate && <p className="text-red-500 text-xs mt-1">{errors.eventDate}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Event Time *</label>
        <input
          type="time"
          name="eventTime"
          value={formData.eventTime}
          onChange={handleInputChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
            errors.eventTime ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.eventTime && <p className="text-red-500 text-xs mt-1">{errors.eventTime}</p>}
      </div>
    </div>

    {availabilityWindow && (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-amber-900">Live Provider Availability</h3>
          <span className="text-xs text-amber-700">Role: {searchRole.replace('_', ' ')}</span>
        </div>

        {availabilityState.loading && (
          <p className="text-sm text-amber-800">Checking available slots...</p>
        )}

        {!availabilityState.loading && availabilityState.error && (
          <p className="text-sm text-red-700">{availabilityState.error}</p>
        )}

        {!availabilityState.loading && !availabilityState.error && availabilityState.searched && (
          <>
            <p className="text-sm text-amber-900 mb-2">
              {availabilityState.slots.length > 0
                ? `${availabilityState.slots.length} slot(s) found for your selected time window.`
                : 'No exact provider slots found for this time window.'}
            </p>

            {availabilityState.slots.length > 0 && (
              <div className="space-y-2 max-h-36 overflow-y-auto">
                {availabilityState.slots.slice(0, 5).map((slot) => (
                  <button
                    key={slot.id}
                    type="button"
                    onClick={() => setSelectedSlotId(slot.id)}
                    className={`w-full text-left bg-white border rounded-md px-3 py-2 text-xs text-gray-700 ${
                      Number(selectedSlotId) === Number(slot.id)
                        ? 'border-amber-500 ring-1 ring-amber-400'
                        : 'border-amber-100'
                    }`}
                  >
                    <div>
                      {new Date(slot.start_time || slot.startTime).toLocaleString()} - {new Date(slot.end_time || slot.endTime).toLocaleString()}
                    </div>
                    {(slot.notes || slot.note) && (
                      <div className="text-gray-500 mt-1">{slot.notes || slot.note}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    )}

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Venue Name *</label>
      <input
        type="text"
        name="venue"
        value={formData.venue}
        onChange={handleInputChange}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
          errors.venue ? 'border-red-500' : 'border-gray-300'
        }`}
        placeholder="Enter venue name"
      />
      {errors.venue && <p className="text-red-500 text-xs mt-1">{errors.venue}</p>}
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Venue Type</label>
      <select
        name="venueType"
        value={formData.venueType}
        onChange={handleInputChange}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
      >
        {VENUE_TYPES.map((type) => (
          <option key={type.value} value={type.value}>
            {type.label}
          </option>
        ))}
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Complete Address *</label>
      <textarea
        name="address"
        value={formData.address}
        onChange={handleInputChange}
        rows={3}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
          errors.address ? 'border-red-500' : 'border-gray-300'
        }`}
        placeholder="Enter complete address"
      />
      {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
        <input
          type="text"
          name="city"
          value={formData.city}
          onChange={handleInputChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
            errors.city ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="City"
        />
        {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
        <input
          type="text"
          name="state"
          value={formData.state}
          onChange={handleInputChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
            errors.state ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="State"
        />
        {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Pincode *</label>
        <input
          type="text"
          name="pincode"
          value={formData.pincode}
          onChange={handleInputChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent ${
            errors.pincode ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Pincode"
        />
        {errors.pincode && <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>}
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Expected Number of Guests</label>
      <select
        name="expectedGuests"
        value={formData.expectedGuests}
        onChange={handleInputChange}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
      >
        {GUEST_COUNT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Special Requirements or Instructions</label>
      <textarea
        name="specialRequirements"
        value={formData.specialRequirements}
        onChange={handleInputChange}
        rows={3}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
        placeholder="Any special requirements, dietary restrictions, or additional information..."
      />
    </div>
  </div>
);

export default BookingStepEvent;
